import React, { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import Button from '../../../components/Button/Button'
import Card from '../../../components/Card/Card'
import ComboInput from '../../../components/ComboInput'
import CollapsibleSection from '../../../components/CollapsibleSection/CollapsibleSection'
import Message from '../../../components/Message/Message'
import Modal from '../../../components/Modal/Modal'
import { ConfirmationModal } from '../../../components/shared'
import {
  WORLD_CUP_LEADERBOARD,
  WORLD_CUP_MATCHES,
  WORLD_CUP_STAGES,
  WORLD_CUP_TEAMS,
} from '../../../graphql/queries'
import {
  CREATE_WORLD_CUP_MATCH,
  UPDATE_WORLD_CUP_MATCH,
  DELETE_WORLD_CUP_MATCH,
  SET_WORLD_CUP_RESULT,
} from '../../../graphql/mutations'
import { DEFAULT_STAGES, emptyMatchForm } from './worldCupAdmin.constants'
import { toLocalDatetimeValue } from './worldCupAdmin.utils'
import { buildWorldCupMatchListSections, sortWorldCupMatchesByKickoff } from '../../WorldCup2026/worldCupMatchSections'
import {
  hasCompleteMatchResult,
  hasMatchStarted,
  isMissingResultMatch,
  isResultFieldFilled,
} from './matchResultStatus'
import useInitialMatchEdit from '../hooks/useInitialMatchEdit'
import useNotification from '../../../hooks/useNotification'
import Notification from '../../../components/Notification/Notification'

const getResultInputState = (homeGoals, awayGoals) => {
  const homeFilled = isResultFieldFilled(homeGoals)
  const awayFilled = isResultFieldFilled(awayGoals)

  if (!homeFilled && !awayFilled) return { complete: false }
  if (homeFilled !== awayFilled) {
    return { error: 'Udfyld begge målfelter, eller lad dem begge stå tomme' }
  }

  const home = Number(homeGoals)
  const away = Number(awayGoals)
  if (!Number.isInteger(home) || home < 0 || !Number.isInteger(away) || away < 0) {
    return { error: 'Indtast et gyldigt resultat (0 eller højere)' }
  }

  return { complete: true, homeGoals: home, awayGoals: away }
}

export default function WorldCupMatchesAdmin({
  initialEditMatchId = null,
  onInitialEditHandled,
}) {
  const { showNotification, notificationProps } = useNotification()
  const [form, setForm] = useState(emptyMatchForm())
  const [matchModalOpen, setMatchModalOpen] = useState(false)
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ isOpen: false, match: null })
  const [collapsedSections, setCollapsedSections] = useState({})

  const { data, loading, refetch } = useQuery(WORLD_CUP_MATCHES, { fetchPolicy: 'cache-and-network' })
  const { refetch: refetchLeaderboard } = useQuery(WORLD_CUP_LEADERBOARD, { skip: true })
  const { data: teamsData } = useQuery(WORLD_CUP_TEAMS, { fetchPolicy: 'cache-and-network' })
  const { data: stagesData } = useQuery(WORLD_CUP_STAGES, { fetchPolicy: 'cache-and-network' })

  const matches = useMemo(() => {
    const list = Array.isArray(data?.worldCupMatches) ? data.worldCupMatches : []
    return sortWorldCupMatchesByKickoff(list)
  }, [data])

  const stages = useMemo(() => {
    const fromDb = stagesData?.worldCupStages
    if (Array.isArray(fromDb) && fromDb.length > 0) {
      return [...fromDb].sort((a, b) => a.sortOrder - b.sortOrder)
    }
    return DEFAULT_STAGES
  }, [stagesData])

  const matchListSections = useMemo(
    () => buildWorldCupMatchListSections(matches, stages),
    [matches, stages]
  )

  const toggleSectionCollapse = useCallback((sectionKey) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionKey]: !(prev[sectionKey] ?? true),
    }))
  }, [])

  const savedTeams = useMemo(() => {
    const fromDb = teamsData?.worldCupTeams?.teams
    return Array.isArray(fromDb) ? fromDb : []
  }, [teamsData])

  const allTeamOptions = useMemo(() => {
    const list = savedTeams.map((t) => t.name).filter(Boolean)
    const ensure = (v) => {
      const val = (v || '').trim()
      if (val && !list.includes(val)) list.unshift(val)
    }
    ensure(form.homeTeam)
    ensure(form.awayTeam)
    return list.sort((a, b) => a.localeCompare(b, 'da-DK'))
  }, [savedTeams, form.homeTeam, form.awayTeam])

  const matchTeamOptions = useMemo(() => {
    const isGroupStage = form.stage?.startsWith('Group ')
    if (!isGroupStage) return allTeamOptions

    const filtered = savedTeams
      .filter((t) => t.group === form.stage)
      .map((t) => t.name)

    return filtered.length > 0 ? filtered.sort((a, b) => a.localeCompare(b, 'da-DK')) : allTeamOptions
  }, [allTeamOptions, savedTeams, form.stage])

  const closeMatchModal = useCallback(() => {
    setMatchModalOpen(false)
    setForm(emptyMatchForm(stages))
  }, [stages])

  const [createMatch] = useMutation(CREATE_WORLD_CUP_MATCH, {
    onCompleted: async () => {
      showNotification('Kamp oprettet', 'success')
      setMatchModalOpen(false)
      setForm(emptyMatchForm(stages))
      await refetch()
    },
    onError: (e) => showNotification(e.message || 'Kunne ikke oprette kamp', 'error'),
  })
  const [updateMatch, { loading: updatingMatch }] = useMutation(UPDATE_WORLD_CUP_MATCH, {
    onError: (e) => showNotification(e.message || 'Kunne ikke opdatere kamp', 'error'),
  })
  const [deleteMatch, { loading: deletingMatch }] = useMutation(DELETE_WORLD_CUP_MATCH, {
    onCompleted: async (_, { variables }) => {
      showNotification('Kamp slettet', 'success')
      setDeleteConfirmModal({ isOpen: false, match: null })
      if (form.id === variables?.id) closeMatchModal()
      await refetch()
    },
    onError: (e) => showNotification(e.message || 'Kunne ikke slette kamp', 'error'),
  })
  const [setResult, { loading: savingResult }] = useMutation(SET_WORLD_CUP_RESULT, {
    onError: (e) => showNotification(e.message || 'Kunne ikke gemme resultat', 'error'),
  })

  const handleStageSelect = (stageName) => {
    const hit = stages.find((s) => s.name === stageName)
    setForm((p) => ({
      ...p,
      stage: stageName,
      sortOrder: hit?.sortOrder ?? p.sortOrder,
    }))
  }

  const submitMatch = async () => {
    if (!form.homeTeam.trim() || !form.awayTeam.trim()) {
      showNotification('Vælg hjemme- og udehold', 'error')
      return
    }
    if (!form.kickoffTime) {
      showNotification('Kickoff er påkrævet', 'error')
      return
    }
    if (!form.stage.trim()) {
      showNotification('Vælg en fase', 'error')
      return
    }

    const stageName = form.stage.trim()
    const stageHit = stages.find((s) => s.name === stageName)
    const input = {
      ...(form.id ? { id: form.id } : {}),
      homeTeam: form.homeTeam.trim(),
      awayTeam: form.awayTeam.trim(),
      kickoffTime: new Date(form.kickoffTime).toISOString(),
      stage: stageName,
      sortOrder: stageHit?.sortOrder ?? Number(form.sortOrder),
      status: form.status,
    }

    if (!form.id) {
      await createMatch({ variables: { input } })
      return
    }

    const resultState = getResultInputState(form.homeGoals, form.awayGoals)
    if (resultState.error) {
      showNotification(resultState.error, 'error')
      return
    }

    try {
      const updateResponse = await updateMatch({ variables: { input } })
      if (updateResponse.errors?.length) return

      if (resultState.complete) {
        const resultResponse = await setResult({
          variables: {
            matchId: form.id,
            homeGoals: resultState.homeGoals,
            awayGoals: resultState.awayGoals,
          },
        })
        if (resultResponse.errors?.length) return
        showNotification('Kamp og resultat gemt — point beregnet', 'success')
        await Promise.all([refetch(), refetchLeaderboard()])
      } else {
        showNotification('Kamp opdateret', 'success')
        await refetch()
      }

      setMatchModalOpen(false)
      setForm(emptyMatchForm(stages))
    } catch {
      // Netværksfejl håndteres i mutationernes onError
    }
  }

  const savingMatch = updatingMatch || savingResult

  const hasMatchResult = (m) => hasCompleteMatchResult(m.homeGoals, m.awayGoals)

  const matchHasStarted = (m) => hasMatchStarted(m.kickoffTime)

  const isMissingResult = (m) => isMissingResultMatch({
    kickoff: m.kickoffTime,
    home: m.homeGoals,
    away: m.awayGoals,
  })

  const isScheduledMatch = (m) => !matchHasStarted(m) && !hasMatchResult(m)

  const getMatchRowClassName = (m) => {
    if (hasMatchResult(m)) return 'worldcup-admin-match-row worldcup-admin-match-row--finished'
    if (isMissingResult(m)) return 'worldcup-admin-match-row worldcup-admin-match-row--missing-result'
    if (isScheduledMatch(m)) return 'worldcup-admin-match-row worldcup-admin-match-row--scheduled'
    return 'worldcup-admin-match-row'
  }

  const formatMatchStatus = (m) => {
    if (isMissingResult(m)) return 'Mangler resultat'
    if (m.status === 'live') return 'Live'
    return 'Planlagt'
  }

  const openNewMatch = () => {
    setForm(emptyMatchForm(stages))
    setMatchModalOpen(true)
  }

  const openMatchForEdit = useCallback((m) => {
    setForm({
      ...m,
      kickoffTime: toLocalDatetimeValue(m.kickoffTime),
      homeGoals: m.homeGoals ?? '',
      awayGoals: m.awayGoals ?? '',
      sortOrder: m.sortOrder ?? stages[0]?.sortOrder ?? 1,
    })
    setMatchModalOpen(true)
  }, [stages])

  useInitialMatchEdit({
    initialEditMatchId,
    matches,
    loading,
    onOpenEdit: openMatchForEdit,
    onInitialEditHandled,
  })

  const openDeleteConfirm = (match) => {
    setDeleteConfirmModal({ isOpen: true, match })
  }

  const closeDeleteConfirm = useCallback(() => {
    if (deletingMatch) return
    setDeleteConfirmModal({ isOpen: false, match: null })
  }, [deletingMatch])

  const confirmDeleteMatch = useCallback(() => {
    const matchId = deleteConfirmModal.match?.id
    if (matchId) deleteMatch({ variables: { id: matchId } })
  }, [deleteConfirmModal.match?.id, deleteMatch])

  const renderMatchRow = (m) => (
    <div key={m.id} className={getMatchRowClassName(m)}>
      <div className="worldcup-admin-match-main">
        <div className="worldcup-admin-match-teams">
          {m.homeTeam} <span className="worldcup-admin-match-vs">vs</span> {m.awayTeam}
        </div>
        <div className="worldcup-admin-match-meta">
          <span>{m.stage}</span>
          <span className="worldcup-admin-match-meta-sep" aria-hidden="true">·</span>
          <time dateTime={m.kickoffTime}>{new Date(m.kickoffTime).toLocaleString('da-DK')}</time>
        </div>
        <div className="worldcup-admin-match-status">
          {hasMatchResult(m) ? (
            <span className="worldcup-admin-match-result">
              Afsluttet · {m.homeGoals}-{m.awayGoals}
            </span>
          ) : (
            <span
              className={`worldcup-admin-match-status-label${
                isMissingResult(m) ? ' worldcup-admin-match-status-label--missing-result' : ''
              }`}
            >
              {formatMatchStatus(m)}
            </span>
          )}
        </div>
      </div>
      <div className="worldcup-admin-match-actions">
        <Button title="Redigér" onClick={() => openMatchForEdit(m)} />
        <Button title="Slet" variant="outlined" color="danger" onClick={() => openDeleteConfirm(m)} />
      </div>
    </div>
  )

  return (
    <>
      <Notification {...notificationProps} />

      <Card className="worldcup-admin-card" hover={false}>
        <div className="worldcup-admin-matches-header">
          <h3>Kampe</h3>
          <Button title="Opret kamp" onClick={openNewMatch} color="primary" />
        </div>
        {matches.length === 0 ? <p className="worldcup-admin-help">Ingen kampe endnu. Tryk &quot;Opret kamp&quot;.</p> : null}
        {matchListSections.map((matchSection) => (
          <CollapsibleSection
            key={matchSection.key}
            title={`${matchSection.title} (${matchSection.matches.length})`}
            isCollapsed={collapsedSections[matchSection.key] ?? true}
            onToggle={() => toggleSectionCollapse(matchSection.key)}
          >
            <div className="worldcup-admin-match-list">
              {matchSection.matches.map(renderMatchRow)}
            </div>
          </CollapsibleSection>
        ))}
      </Card>

      <Modal
        isOpen={matchModalOpen}
        onClose={closeMatchModal}
        title={form.id ? 'Redigér kamp' : 'Opret kamp'}
        size="medium"
        className="worldcup-admin-modal"
      >
        <div className="worldcup-admin-match-modal">
          {allTeamOptions.length === 0 ? (
            <Message type="warning">Gem VM-hold først, så du kan vælge dem i kampformularen.</Message>
          ) : null}
          {form.stage?.startsWith('Group ') && matchTeamOptions.length < allTeamOptions.length ? (
            <Message type="info">Viser kun hold i {form.stage}.</Message>
          ) : null}
          <div className="worldcup-admin-form-grid">
            <label className="worldcup-admin-field">
              <span className="worldcup-admin-label">Hjemmehold</span>
              <ComboInput
                id="wc-home-team"
                value={form.homeTeam}
                options={matchTeamOptions}
                placeholder="Vælg hjemmehold"
                ariaLabel="Hjemmehold"
                onChange={(v) => setForm((p) => ({ ...p, homeTeam: v }))}
              />
            </label>
            <label className="worldcup-admin-field">
              <span className="worldcup-admin-label">Udehold</span>
              <ComboInput
                id="wc-away-team"
                value={form.awayTeam}
                options={matchTeamOptions}
                placeholder="Vælg udehold"
                ariaLabel="Udehold"
                onChange={(v) => setForm((p) => ({ ...p, awayTeam: v }))}
              />
            </label>
            <label className="worldcup-admin-field">
              <span className="worldcup-admin-label">Kickoff</span>
              <input
                className="worldcup-admin-input"
                type="datetime-local"
                value={form.kickoffTime}
                onChange={(e) => setForm((p) => ({ ...p, kickoffTime: e.target.value }))}
              />
            </label>
            <label className="worldcup-admin-field">
              <span className="worldcup-admin-label">Fase</span>
              <select
                className="worldcup-admin-input"
                value={form.stage}
                onChange={(e) => handleStageSelect(e.target.value)}
              >
                {stages.map((stage) => (
                  <option key={stage.name} value={stage.name}>{stage.name}</option>
                ))}
              </select>
            </label>
          </div>
          {form.id ? (
            <div className="worldcup-admin-result-section">
              <h4 className="worldcup-admin-result-title">Resultat</h4>
              <p className="worldcup-admin-help">
                Udfyld resultat for at afslutte kampen og beregne point. Lad felterne stå tomme, hvis kampen endnu ikke er spillet.
              </p>
              <div className="worldcup-admin-form-grid worldcup-admin-form-grid--result">
                <label className="worldcup-admin-field">
                  <span className="worldcup-admin-label">{form.homeTeam || 'Hjemmemål'}</span>
                  <input
                    className="worldcup-admin-input"
                    type="number"
                    min={0}
                    value={form.homeGoals}
                    onChange={(e) => setForm((p) => ({ ...p, homeGoals: e.target.value }))}
                  />
                </label>
                <label className="worldcup-admin-field">
                  <span className="worldcup-admin-label">{form.awayTeam || 'Udemål'}</span>
                  <input
                    className="worldcup-admin-input"
                    type="number"
                    min={0}
                    value={form.awayGoals}
                    onChange={(e) => setForm((p) => ({ ...p, awayGoals: e.target.value }))}
                  />
                </label>
              </div>
            </div>
          ) : null}
          <div className="worldcup-admin-form-actions worldcup-admin-form-actions--match">
            <Button
              title="Annuller"
              variant="outlined"
              className="worldcup-admin-modal-cancel"
              onClick={closeMatchModal}
              disabled={savingMatch}
            />
            <Button
              title={form.id ? 'Gem' : 'Opret kamp'}
              onClick={submitMatch}
              color="confirmation"
              loading={savingMatch}
              loadingText="Gemmer..."
            />
          </div>
        </div>
      </Modal>

      <ConfirmationModal
        isOpen={deleteConfirmModal.isOpen}
        onClose={closeDeleteConfirm}
        onConfirm={confirmDeleteMatch}
        title="Slet kamp"
        message={
          deleteConfirmModal.match
            ? `Er du sikker på, at du vil slette kampen ${deleteConfirmModal.match.homeTeam} vs ${deleteConfirmModal.match.awayTeam}? Denne handling kan ikke fortrydes.`
            : 'Er du sikker på, at du vil slette denne kamp? Denne handling kan ikke fortrydes.'
        }
        confirmText="Slet"
        cancelText="Annuller"
        confirmButtonType="danger"
        isLoading={deletingMatch}
      />
    </>
  )
}
